/** Per-app hosts for the SOR Details page; each app registers its own at root, none borrows another's. */
import { HttpClient } from '@angular/common/http';
import { inject, Provider } from '@angular/core';
import { ActivityEntityName, CLIENT_SOR_TASK_COMMAND_ID, getJobSectionRights, JobItemCommandId, jobManageRights, JobSecurityRight, Party, getPortalContractorSectionRights } from '@jms/model';
import type { CatalogueCode, PurchaseOrder, StageItemDetail, Task } from '@jms/model';
import { ContractorActivityService } from '@jms/portal/module.portal.contractor.operations/src/services';
import { CustomFieldService } from '@jms/ui/ui.custom.fields.runtime';
import { PricingService } from '@jms/ui/ui.standalone.services/pricing.service';
import { RightsService } from '@jms/utils/util.permissions';
import { EntityDataServiceRaw } from '@workbuddy/services/entities';
import { jobTaskPermission } from '@workbuddy/ui/modules/tasks/src/models';
import { embeddedItemCommandError } from '@workbuddy/surfaces/embedded-item';
import { taskCompletionRefusalDetail } from '@workbuddy/entities/work.task/task.completion.refusal';
import { ActivityHost } from '@workbuddy/ui/modules/activities';
import { catchError, lastValueFrom, map, of } from 'rxjs';
import { jobItemReadonly, resolveItemFieldReadonly } from '../../../models/job.item.field.rules';
import { buildContractorItemRowCommands, requestContractorItemLine } from '../../commands/contractor.item.row.commands';
import { buildJobItemRowCommands } from '../../commands/item.row.commands';
import { repriceItemField } from '../../support/item.edit.helpers';
import { ItemDetailHost, ItemDetailHostScope, ITEM_DETAIL_HOST, READ_ONLY_FIELDS } from './item.detail.host';

function serverMessage(error: unknown): string | undefined {

  return (error as { error?: { message?: string } })?.error?.message;
}

/** The menu factories read a board row: the line under its stage, the stage under its job. */
function itemRow(detail: StageItemDetail, scope: ItemDetailHostScope): Record<string, unknown> {

  const stageId = scope.stageId ?? detail.stage?._id;

  return {
    ...detail,
    // Defect dialog uses this as the stage display name (board: workOrderNumber).
    workOrders: { _id: stageId, workOrderNumber: detail.stage?.name },
  };
}

/** The web app: Jobs and Tasks rights, the tenant catalogue, pricebook and purchase orders, and the office task editor. */
export const webItemDetailHostProvider: Provider = {
  provide: ITEM_DETAIL_HOST,
  useFactory: (): ItemDetailHost => {

    const rights = inject(RightsService);
    const http = inject(HttpClient);
    const entityData = inject(EntityDataServiceRaw);
    const pricingService = inject(PricingService);
    const customFieldService = inject(CustomFieldService);

    // Getters: the role changes on sign-in without a reload, and the page reads them once when it opens.
    return {
      party: Party.Office,
      get canManageJob() {
        return rights.hasRights(...jobManageRights);
      },
      // The job editors' Tasks rule (Tasks module rights AND the job's Tasks section rights); the rail adds no tasks.
      get taskPermission() {
        return { ...jobTaskPermission(rights.role), addTask: false };
      },
      visible: { statusPicker: true, sellPrice: true, buyPrice: true, extPrices: true, margin: true, financial: true, customFields: true, approveDecline: true, workAlreadyCompleted: true, activities: true, completedBy: true, evidenceBuckets: true },
      buyPriceLabel: 'Buy Price',
      activityService: null,
      activityHost: ActivityHost.Tenant,
      fieldReadonly: (_detail, facts) => resolveItemFieldReadonly(facts),
      // The office may request any Draft, whoever raised it (WBS-12206); the layout already limits the button to a Draft.
      canRequest: () => rights.hasRights(...jobManageRights),
      refusal: serverMessage,
      activityRights: () => ({ manageActivity: rights.hasRights(JobSecurityRight.ManageActivity), manageOtherUserActivity: rights.hasRights(JobSecurityRight.ManageOtherUserActivity) }),
      // By name: entity-data `getById` takes an `_id`, and codes key on a uuid, so it matched nothing.
      catalogueCode: (name, fields) => http
        .get<Partial<CatalogueCode>>(`/api/codes/${encodeURIComponent(name)}/infos/${fields.join(';')}`)
        .pipe(catchError(() => of(null))),
      purchaseOrderStatus: id => entityData
        .getById<PurchaseOrder>(ActivityEntityName.PurchaseOrder, id)
        .pipe(map(response => response?.item?.status ?? null)),
      reprice: args => repriceItemField({ ...args, pricingService }),
      customFieldMappings: parent => customFieldService.getCustomFieldMapping(parent),
      // SOR-only and the web SORs rights travel as options: this page opens outside the board, so it has no mount bag.
      // Request SOR / Approve stay off the ⋮: the page header already offers them.
      menuCommands: (detail, scope, params, onDeleted, onStatusChanged) => buildJobItemRowCommands(itemRow(detail, scope), params, { sorOnly: true, onDeleted, onStatusChanged, sectionRights: () => getJobSectionRights(rights.role).sors, pendingCommands: false }),
      requestItem: async detail => {
        try {
          await lastValueFrom(entityData.executeCommand(ActivityEntityName.JobItem, JobItemCommandId.RequestItem, { itemId: detail._id }));
          return null;
        } catch (error: unknown) {
          return { reason: serverMessage(error) };
        }
      },
      taskRail: () => import('./item.detail.task.surface.rail.component').then(m => m.ItemDetailTaskSurfaceRailComponent),
      taskRailRead: null,
      readOnly: false,
      opensByAddress: false,
    };
  },
};

/** The Contractor Portal: the server stamps what a subcontractor may change and holds the Contractor party rules, so this host never reaches a tenant-only route. */
export const contractorPortalItemDetailHostProvider: Provider = {
  provide: ITEM_DETAIL_HOST,
  useFactory: (): ItemDetailHost => {

    const entityData = inject(EntityDataServiceRaw);
    const sections = getPortalContractorSectionRights(inject(RightsService).role);

    return {
      party: Party.Contractor,
      canManageJob: false,
      // A SOR's task is a task: its status and photos need the portal Tasks › Edit; `editableBy` and `assertCanReopen` refuse the rest on the server (WBS-12242).
      taskPermission: { addTask: false, editTask: sections.tasks.edit },
      // The tick stays editable only on the subcontractor's own Draft: the layout reads the server's `canUpdate`, which holds the author window, the company and SORs Edit.
      visible: { statusPicker: false, sellPrice: false, buyPrice: true, extPrices: false, margin: false, financial: false, customFields: false, approveDecline: false, workAlreadyCompleted: true, activities: true, completedBy: true, evidenceBuckets: true },
      buyPriceLabel: 'Unit Price',
      activityService: () => new ContractorActivityService(),
      activityHost: ActivityHost.ContractorPortal,
      fieldReadonly: detail => jobItemReadonly(detail, { contractor: true }),
      // The server's `canUpdate` holds the Draft author window and the company check, as on the portal board's row menu.
      canRequest: detail => detail?.capabilities?.canUpdate === true,
      // A refused completion names each missing section and open sub-task; a subcontractor has no override.
      refusal: error => taskCompletionRefusalDetail(error) ?? embeddedItemCommandError(error).message,
      // The portal note route refuses a closed stage, and a subcontractor never edits another user's note.
      activityRights: stageOpenForContractor => ({ manageActivity: stageOpenForContractor, manageOtherUserActivity: false }),
      // No catalogue, purchase order or pricebook route on the portal: the server prices a contractor line when it writes.
      catalogueCode: () => of(null),
      purchaseOrderStatus: () => of(null),
      reprice: () => Promise.resolve(null),
      customFieldMappings: () => Promise.resolve([]),
      // Request is the header button, so the menu keeps only Delete of their own Draft.
      menuCommands: (detail, scope, params, onDeleted) => buildContractorItemRowCommands({ _id: scope.jobId ?? detail.stage?.jobId, workOrders: { _id: scope.stageId ?? detail.stage?._id, codes: detail } }, { itemsChanged: params?.itemsChanged ?? (() => undefined), sorOnly: () => true }, { request: false, onDeleted }),
      requestItem: (detail, scope) => requestContractorItemLine(entityData, { itemId: detail._id, jobId: scope.jobId ?? detail.stage?.jobId, stageId: scope.stageId ?? detail.stage?._id }),
      taskRail: () => import('./item.detail.task.surface.rail.component').then(m => m.ItemDetailTaskSurfaceRailComponent),
      taskRailRead: null,
      readOnly: false,
      opensByAddress: true,
    };
  },
};

/** The Client Portal: the client follows the work, so the page shows sell, completion and SOR evidence and never writes. */
export const clientPortalItemDetailHostProvider: Provider = {
  provide: ITEM_DETAIL_HOST,
  useFactory: (): ItemDetailHost => {

    const entityData = inject(EntityDataServiceRaw);

    return {
      party: Party.Customer,
      canManageJob: false,
      taskPermission: { addTask: false, editTask: false },
      visible: { statusPicker: false, sellPrice: true, buyPrice: false, extPrices: false, margin: false, financial: false, customFields: false, approveDecline: false, workAlreadyCompleted: false, activities: false, completedBy: false, evidenceBuckets: false },
      buyPriceLabel: 'Buy Price',
      activityService: null,
      activityHost: ActivityHost.ClientPortal,
      fieldReadonly: () => READ_ONLY_FIELDS,
      canRequest: () => false,
      refusal: () => undefined,
      activityRights: () => ({ manageActivity: false, manageOtherUserActivity: false }),
      catalogueCode: () => of(null),
      purchaseOrderStatus: () => of(null),
      reprice: () => Promise.resolve(null),
      customFieldMappings: () => Promise.resolve([]),
      menuCommands: () => [],
      requestItem: () => Promise.resolve({}),
      // The client task read leaves SOR-tree tasks to this page, so the rail previews a sub-task through the line's own read (WBS-12873).
      taskRail: () => import('./item.detail.task.surface.rail.component').then(m => m.ItemDetailTaskSurfaceRailComponent),
      taskRailRead: itemId => ({
        operation: 'preview',
        loadTask: taskId => lastValueFrom(entityData.executeCommand<Task>(ActivityEntityName.Task, CLIENT_SOR_TASK_COMMAND_ID, { itemId, taskId })).catch(() => null),
      }),
      readOnly: true,
      opensByAddress: false,
    };
  },
};
